import { type ApiEnvelope, api, unwrap } from "@/lib/api";

import type {
	CustomerDetail,
	CustomersFilter,
	CustomersStats,
	GdprRequest,
	LoyaltyReward,
	LoyaltyTier,
	PaginatedCustomers,
	Referral,
	Segment,
} from "./types";

const toQs = (filter: Record<string, unknown>): string => {
	const usp = new URLSearchParams();
	for (const [k, v] of Object.entries(filter)) {
		if (v === undefined || v === null || v === "") continue;
		usp.set(k, String(v));
	}
	const qs = usp.toString();
	return qs ? `?${qs}` : "";
};

export const customersApi = {
	list: (filter: CustomersFilter = {}) =>
		unwrap<PaginatedCustomers>(
			api.get<ApiEnvelope<PaginatedCustomers>>(
				`/admin/customers${toQs(filter as Record<string, unknown>)}`,
			),
		),

	get: (id: string, reason?: string) =>
		unwrap<CustomerDetail>(
			api.get<ApiEnvelope<CustomerDetail>>(
				`/admin/customers/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`,
			),
		),

	create: (payload: Record<string, unknown>) =>
		unwrap<CustomerDetail>(
			api.post<ApiEnvelope<CustomerDetail>>("/admin/customers", payload),
		),

	update: (id: string, payload: Record<string, unknown>) =>
		unwrap<CustomerDetail>(
			api.patch<ApiEnvelope<CustomerDetail>>(`/admin/customers/${id}`, payload),
		),

	suspend: (id: string, reason: string) =>
		api.post(`/admin/customers/${id}/suspend`, { reason }).then((r) => r.data),

	reactivate: (id: string) =>
		api.post(`/admin/customers/${id}/reactivate`, {}).then((r) => r.data),

	anonymize: (id: string, reason: string, approvalId?: string) =>
		api
			.post(`/admin/customers/${id}/anonymize`, { reason, approvalId })
			.then((r) => r.data),

	merge: (sourceId: string, targetId: string, reason: string, approvalId?: string) =>
		api
			.post("/admin/customers/merge", { sourceId, targetId, reason, approvalId })
			.then((r) => r.data),

	exportCsv: (filter: CustomersFilter = {}, approvalId?: string) =>
		api
			.post(
				"/admin/customers/export",
				{ ...filter, approvalId },
				{ responseType: "blob" },
			)
			.then((r) => r.data),

	addNote: (id: string, content: string) =>
		api.post(`/admin/customers/${id}/notes`, { content }).then((r) => r.data),

	addTag: (id: string, label: string, color?: string) =>
		api
			.post(`/admin/customers/${id}/tags`, { label, color })
			.then((r) => r.data),

	removeTag: (id: string, label: string) =>
		api.delete(`/admin/customers/${id}/tags/${encodeURIComponent(label)}`).then((r) => r.data),

	updateConsents: (id: string, consents: Array<{ channel: string; granted: boolean }>) =>
		api
			.patch(`/admin/customers/${id}/consents`, { consents })
			.then((r) => r.data),

	listPiiAccess: (id: string) =>
		unwrap<Array<{ id: string; actorLabel: string; reason?: string | null; createdAt?: string; at?: string }>>(
			api.get<ApiEnvelope<Array<{ id: string; actorLabel: string; reason?: string | null; createdAt?: string; at?: string }>>>(
				`/admin/customers/${id}/pii-access`,
			),
		),

	adjustLoyalty: (
		id: string,
		points: number,
		reason: string,
		approvalId?: string,
	) =>
		api
			.post(`/admin/customers/${id}/loyalty/adjust`, {
				points,
				reason,
				approvalId,
			})
			.then((r) => r.data),

	loyaltyHistory: (id: string) =>
		unwrap<unknown[]>(
			api.get<ApiEnvelope<unknown[]>>(`/admin/customers/${id}/loyalty/history`),
		),

	// Loyalty config
	tiers: () =>
		unwrap<LoyaltyTier[]>(
			api.get<ApiEnvelope<LoyaltyTier[]>>("/admin/loyalty/tiers"),
		),
	updateTier: (id: string, payload: Record<string, unknown>) =>
		unwrap<LoyaltyTier>(
			api.patch<ApiEnvelope<LoyaltyTier>>(`/admin/loyalty/tiers/${id}`, payload),
		),
	rewards: () =>
		unwrap<LoyaltyReward[]>(
			api.get<ApiEnvelope<LoyaltyReward[]>>("/admin/loyalty/rewards"),
		),
	createReward: (payload: Record<string, unknown>) =>
		unwrap<unknown>(
			api.post<ApiEnvelope<unknown>>("/admin/loyalty/rewards", payload),
		),
	liability: () =>
		unwrap<{
			totalPoints: number;
			accounts: number;
			byTier: Record<string, number>;
			accountsByTier?: Record<string, number>;
		}>(
			api.get<
				ApiEnvelope<{
					totalPoints: number;
					accounts: number;
					byTier: Record<string, number>;
					accountsByTier?: Record<string, number>;
				}>
			>("/admin/loyalty/liability"),
		),

	// Segments
	segments: () =>
		unwrap<Segment[]>(api.get<ApiEnvelope<Segment[]>>("/admin/segments")),
	segmentPreview: (rules: unknown) =>
		unwrap<{ count: number; ms: number }>(
			api.post<ApiEnvelope<{ count: number; ms: number }>>(
				"/admin/segments/preview",
				{ rules },
			),
		),
	createSegment: (payload: Record<string, unknown>) =>
		unwrap<Segment>(api.post<ApiEnvelope<Segment>>("/admin/segments", payload)),
	updateSegment: (id: string, payload: Record<string, unknown>) =>
		unwrap<Segment>(api.patch<ApiEnvelope<Segment>>(`/admin/segments/${id}`, payload)),
	deleteSegment: (id: string) =>
		api.delete(`/admin/segments/${id}`).then((r) => r.data),
	segmentMembers: (id: string, offset = 0, limit = 50) =>
		unwrap<{ items: Array<{ id: string; name: string; email?: string; phone: string }>; total: number }>(
			api.get<ApiEnvelope<{ items: Array<{ id: string; name: string; email?: string; phone: string }>; total: number }>>(
				`/admin/segments/${id}/members?offset=${offset}&limit=${limit}`,
			),
		),
	importStaticSegment: (id: string, customerIds: string[]) =>
		api.post(`/admin/segments/${id}/import`, { customerIds }).then((r) => r.data),

	// Referrals
	referrals: (status?: string) =>
		unwrap<Referral[]>(
			api.get<ApiEnvelope<Referral[]>>(
				`/admin/referrals${status ? `?status=${status}` : ""}`,
			),
		),
	leaderboard: () =>
		unwrap<Array<{ sponsorId: string; total: number; rewarded: number }>>(
			api.get<
				ApiEnvelope<
					Array<{ sponsorId: string; total: number; rewarded: number }>
				>
			>("/admin/referrals/leaderboard"),
		),
	referralConfig: () =>
		unwrap<Record<string, unknown>>(
			api.get<ApiEnvelope<Record<string, unknown>>>("/admin/referrals/config"),
		),
	updateReferralConfig: (payload: Record<string, unknown>) =>
		unwrap<Record<string, unknown>>(
			api.patch<ApiEnvelope<Record<string, unknown>>>("/admin/referrals/config", payload),
		),

	// GDPR
	gdprRequests: (status?: string) =>
		unwrap<GdprRequest[]>(
			api.get<ApiEnvelope<GdprRequest[]>>(
				`/admin/gdpr/requests${status ? `?status=${status}` : ""}`,
			),
		),
	gdprAlerts: () =>
		unwrap<GdprRequest[]>(
			api.get<ApiEnvelope<GdprRequest[]>>("/admin/gdpr/requests/alerts"),
		),
	gdprSlaReport: () =>
		unwrap<{ total: number; onTime: number; overdue: number; rate: number }>(
			api.get<
				ApiEnvelope<{
					total: number;
					onTime: number;
					overdue: number;
					rate: number;
				}>
			>("/admin/gdpr/sla-report"),
		),
	processGdpr: (id: string, status: string, notes?: string) =>
		api
			.post(`/admin/gdpr/requests/${id}/process`, { status, notes })
			.then((r) => r.data),

	// Stats
	stats: () =>
		unwrap<CustomersStats>(
			api.get<ApiEnvelope<CustomersStats>>("/admin/customers/stats"),
		),
	statsRetention: () =>
		unwrap<Array<{ cohort: string; size: number; retention_30: number; retention_60: number; retention_90: number }>>(
			api.get<ApiEnvelope<Array<{ cohort: string; size: number; retention_30: number; retention_60: number; retention_90: number }>>>(
				"/admin/customers/stats/retention",
			),
		),
	statsChurn: () =>
		unwrap<Array<{ tier: string; total: number; churned: number }>>(
			api.get<ApiEnvelope<Array<{ tier: string; total: number; churned: number }>>>(
				"/admin/customers/stats/churn",
			),
		),
};
